import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { Card } from '../components/ui/Card'
import { landingPathForRole } from '../lib/api/auth'
import { useAuth } from '../lib/auth'

/**
 * Magic-link landing.
 *
 * Supabase's `detectSessionInUrl: true` exchanges the PKCE code on client
 * init, so by the time we mount we either have a session or an error. We
 * then route the user based on:
 *
 *   1. `?invite=<token>` — back to /accept-invite/<token> so the actor flow
 *      completes (highest priority).
 *   2. `?next=<path>`    — explicit caller-supplied destination.
 *   3. role-based default — actor → /actor, supervisor → /supervisor,
 *      owner|member → /producer, otherwise /account.
 */
const callbackSearchSchema = z.object({
  invite: z.string().optional(),
  next: z.string().optional(),
})

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
  validateSearch: callbackSearchSchema,
})

function AuthCallback() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/auth/callback' })
  const { session, role, loading } = useAuth()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (loading) return
    if (!session) {
      setError('Sign-in link expired or already used. Request a new one.')
      return
    }
    let target: string
    if (search.invite) {
      target = `/accept-invite/${search.invite}`
    } else if (search.next) {
      target = search.next
    } else {
      target = landingPathForRole(role)
    }
    void navigate({ to: target, replace: true })
  }, [loading, session, role, search.invite, search.next, navigate])

  return (
    <Card style={{ maxWidth: 420, margin: '4rem auto' }}>
      <h1 style={{ marginTop: 0 }}>Signing you in...</h1>
      {error ? (
        <p style={{ color: '#b91c1c' }}>{error}</p>
      ) : (
        <p style={{ color: '#6b7280' }}>One moment.</p>
      )}
    </Card>
  )
}
